import type { FC } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  initialValuesByProductType,
  SBU_UNIT,
} from 'src/apis/storage/mappings';
import type { PublicStorageLocationsRequest } from 'src/apis/storage/use-public-storage-locations';
import type { PublicStorageOffersRequest } from 'src/apis/storage/use-public-storage-offers';
import { useStorageOfferCreate } from 'src/apis/storage/use-storage-offer-create';
import type { StorageServiceRootRequest } from 'src/apis/storage/use-storage-service-root';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Form } from 'src/components/form/form';
import { PageHeader } from 'src/components/layout/page-header';
import type { Successful } from 'src/hooks/use-axios';
import { useNow } from 'src/hooks/use-now';
import { useReferrer } from 'src/hooks/use-referrer';
import { useTitle } from 'src/hooks/use-title';
import { StorageOfferFields } from 'src/pages/storage/offers-new/create/fields';
import {
  formValuesToWriteData,
  getStorageOfferSchema,
} from 'src/pages/storage/offers-new/create/schema';
import type { StorageOfferFormValues } from 'src/pages/storage/offers-new/create/schema';
import { Forbidden } from 'src/utils/errors';

type Props = {
  storageServiceRoot: Successful<StorageServiceRootRequest>;
  storageLocations: Successful<PublicStorageLocationsRequest>;
  storageOffers: Successful<PublicStorageOffersRequest>;
};

export const StorageOfferCreateForm: FC<Props> = ({
  storageServiceRoot,
  storageLocations,
  storageOffers,
}) => {
  const title = 'Create Storage Offer';
  useTitle(title);

  const referrer = useReferrer({
    label: 'Storage Offers',
    location: '/storage/offers-new',
  });

  const createOffer = useStorageOfferCreate();
  const navigate = useNavigate();
  const now = useNow();

  const createOfferLink = storageOffers.response.data._links.createStorageOffer;
  const prepareFileUploadLink =
    storageServiceRoot.response.data._links.prepareFileUpload;

  if (!createOfferLink || !prepareFileUploadLink) {
    throw new Forbidden();
  }

  useEffect(() => {
    if (!createOffer.response) return;
    toast.success('Storage offer was successfully created.');
    navigate('/storage/offers-new');
    // oxlint-disable-next-line exhaustive-deps
  }, [createOffer.response]);

  return (
    <div className="grid items-center gap-xs">
      <PageHeader title={title} referrer={referrer} />

      <Card data-testid="create-storage-offer-form">
        <Form
          initialValues={{
            name: null,
            storageLocationId: null,
            productPeriod: null,
            productRuntimeStart: null,
            productRuntimeEnd: null,
            publication: null,
            biddingWindowStart: null,
            biddingWindowEnd: null,
            firstRoundBiddingWindowStart: null,
            biddingWindowDurationHours: null,
            biddingWindowDurationMinutes: null,
            timeBetweenBiddingWindowsHour: null,
            timeBetweenBiddingWindowsMinute: null,
            productType: SBU_UNIT,
            typeOfProductUnit:
              initialValuesByProductType[SBU_UNIT].typeOfProductUnit,
            allocationMethod: null,
            productName: null,
            availableQuantity: null,
            priceTimeUnit: 'RUNTIME',
            reservePrice: null,
            productComponents: [],
            offerSpecificNote: null,
            attachedFiles: [{ file: null }],
          }}
          schema={(values: StorageOfferFormValues) =>
            getStorageOfferSchema({
              now,
              publication: values.publication,
              biddingWindowStart: values.biddingWindowStart,
              biddingWindowEnd: values.biddingWindowEnd,
              productRuntimeStart: values.productRuntimeStart,
              productRuntimeEnd: values.productRuntimeEnd,
              productPeriod: values.productPeriod,
              allocationMethod: values.allocationMethod,
              biddingWindowDurationHours: values.biddingWindowDurationHours,
              biddingWindowDurationMinutes: values.biddingWindowDurationMinutes,
              timeBetweenBiddingWindowsHour:
                values.timeBetweenBiddingWindowsHour,
              timeBetweenBiddingWindowsMinute:
                values.timeBetweenBiddingWindowsMinute,
            })
          }
          onSubmit={(values) => {
            const data = formValuesToWriteData(values);
            createOffer.execute({
              url: createOfferLink.href,
              data,
            });
          }}
          constraintViolation={createOffer.error}
          cancelOrResetButton={referrer}
          submitButton={
            <Button
              type="submit"
              disabled={createOffer.pending}
              data-testid="create-storage-offer"
            >
              {createOffer.pending ? 'Pending...' : 'Create Offer'}
            </Button>
          }
        >
          <StorageOfferFields
            storageLocations={storageLocations}
            prepareFileUploadLink={prepareFileUploadLink}
          />
        </Form>
      </Card>
    </div>
  );
};
